import { Box, ButtonBase, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { ExitToApp, VisibilityOutlined } from '@material-ui/icons';
import { observer } from 'mobx-react';
import { useUserStore } from '../../hooks';
import { IMPERSONATION_BAR_HEIGHT, IMPERSONATION_FRAME_GAP } from '../../modules/portalConfiguration/hooks/useHeaderHeight';

const IMPERSONATE_COLOR = '#E5484D';

const useStyles = makeStyles(theme => ({
  // red frame around the whole window: a constant reminder you're acting as someone else
  frame: {
    position: 'fixed',
    inset: 0,
    border: `3px solid ${IMPERSONATE_COLOR}`,
    borderRadius: 16,
    pointerEvents: 'none',
    zIndex: theme.zIndex.modal + 10,
  },
  // bar inside the frame, above the floating navigation
  bar: {
    position: 'fixed',
    top: IMPERSONATION_FRAME_GAP,
    left: IMPERSONATION_FRAME_GAP,
    right: IMPERSONATION_FRAME_GAP,
    height: IMPERSONATION_BAR_HEIGHT,
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    padding: theme.spacing(0, 1.5),
    borderRadius: 12,
    backgroundColor: IMPERSONATE_COLOR,
    color: '#fff',
    zIndex: theme.zIndex.appBar + 1,
  },
  icon: {
    fontSize: 20,
  },
  avatar: {
    flexShrink: 0,
    width: 28,
    height: 28,
    borderRadius: '50%',
    backgroundColor: 'rgba(255, 255, 255, 0.22)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 12,
    fontWeight: 600,
  },
  text: {
    minWidth: 0,
    flex: 1,
    lineHeight: 1.3,
  },
  title: {
    fontSize: 14,
    fontWeight: 600,
    color: '#fff',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  subtitle: {
    fontSize: 12,
    color: 'rgba(255, 255, 255, 0.85)',
  },
  exit: {
    flexShrink: 0,
    gap: 6,
    padding: theme.spacing(0.75, 1.75),
    borderRadius: 999,
    backgroundColor: '#fff',
    color: IMPERSONATE_COLOR,
    fontSize: 13,
    fontWeight: 600,
    '&:hover': {
      backgroundColor: '#FFF1F2',
    },
    '& svg': {
      fontSize: 18,
    },
  },
}));

const getInitials = (name: string) =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map(part => part[0].toUpperCase())
    .join('');

export const ImpersonateHeader = observer(({ show, onExit }) => {
  const classes = useStyles();
  const { userStore } = useUserStore();
  const name = userStore?.userInformation?.fullName ?? '';
  const company = (userStore?.userInformation as any)?.companyName;

  if (!show || !name) {
    return null;
  }

  return (
    <>
      <Box className={classes.frame} />
      <Box className={classes.bar} role="status">
        <VisibilityOutlined className={classes.icon} />
        <Box className={classes.avatar}>{getInitials(name)}</Box>
        <Box className={classes.text}>
          <Typography className={classes.title}>
            Impersonating {name}{company ? ` · ${company}` : ''}
          </Typography>
          <Typography className={classes.subtitle}>
            Changes you make are saved as them
          </Typography>
        </Box>
        <ButtonBase
          className={classes.exit}
          onClick={e => {
            e.preventDefault();
            onExit();
          }}
        >
          <ExitToApp />
          Exit view
        </ButtonBase>
      </Box>
    </>
  );
});
